import { FormattedTextToken } from './props';

/**
 * Unicode Private Use Area (U+E000–U+F8FF): code points that the Unicode
 * standard deliberately leaves unassigned so applications can use them as
 * internal sentinels without colliding with real characters.
 *
 * PLACEHOLDER_CODE_BASE is the first PUA code point (U+E000). Each token
 * (mention, URL, etc.) gets replaced with a single character starting here
 * (U+E000, U+E001, …) before markdown parsing, preventing the parser from
 * splitting multi-character tokens across its internal boundaries.
 *
 * MAX_PLACEHOLDERS stops just before U+F900, where CJK Compatibility
 * Ideographs begin — a real Unicode block we must not overwrite.
 */
export const PLACEHOLDER_CODE_BASE = 0xe000;

const MAX_PLACEHOLDERS = 0xf900 - PLACEHOLDER_CODE_BASE;

export type EncodedPlaceholders = {
  encoded: string;
  tokenByCharCode: Map<number, FormattedTextToken>;
};

/**
 * Replaces token substrings left-to-right (longest value first at each position)
 * with single private-use characters so markdown parsing cannot split mentions/URLs.
 */
export function encodeTokenPlaceholders(
  text: string,
  tokens: FormattedTextToken[],
): EncodedPlaceholders {
  if (tokens.length === 0) {
    return { encoded: text, tokenByCharCode: new Map() };
  }

  const sorted = [...tokens].sort((a, b) => b.value.length - a.value.length);
  const tokenByCharCode = new Map<number, FormattedTextToken>();
  let out = '';
  let i = 0;
  let phIdx = 0;

  while (i < text.length) {
    let matched: FormattedTextToken | undefined;
    for (const token of sorted) {
      if (token.value.length > 0 && text.startsWith(token.value, i)) {
        matched = token;
        break;
      }
    }
    if (matched) {
      if (phIdx >= MAX_PLACEHOLDERS) {
        out += text[i];
        i++;
        continue;
      }
      const code = PLACEHOLDER_CODE_BASE + phIdx;
      phIdx++;
      tokenByCharCode.set(code, matched);
      out += String.fromCharCode(code);
      i += matched.value.length;
    } else {
      out += text[i];
      i++;
    }
  }

  return { encoded: out, tokenByCharCode };
}
